import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import PaymentDialog from "@/components/PaymentDialog";
import PowerTranzDialog from "@/components/PowerTranzDialog";

const methodLabelKey = {
    stripe: "book.payCard",
    paypal: "book.payPaypal",
    powertranz: "book.payPowertranz",
};

function MethodPicker({ methods, selected, onSelect, t }) {
    return (
        <div className="space-y-2" data-testid="pay-balance-methods">
            {methods.map((m) => (
                <button
                    key={m.key}
                    type="button"
                    onClick={() => onSelect(m.key)}
                    data-testid={`pay-balance-method-${m.key}`}
                    className={`w-full text-left border px-4 py-3 text-sm transition-all ${selected === m.key ? "border-forest bg-forest/5" : "border-espresso/15 hover:border-espresso/40"}`}
                >
                    <span className="inline-flex items-center gap-2">
                        {selected === m.key && <Check size={14} className="text-forest" />}
                        {t(methodLabelKey[m.key] || m.key)}
                    </span>
                </button>
            ))}
        </div>
    );
}

/**
 * Lets a guest complete payment on an existing pending booking ("Reserve now,
 * pay later"). Reuses the same Stripe / PowerTranz / PayPal rails as checkout.
 */
export default function PayBalanceDialog({ booking, methods = [], open, onOpenChange, onPaid }) {
    const { t } = useT();
    const { format: formatMoney } = useCurrency();
    const [selected, setSelected] = useState(methods[0]?.key || "");
    const [working, setWorking] = useState(false);
    const [checkout, setCheckout] = useState(null); // stripe { booking, clientSecret, publishableKey }
    const [ptCheckout, setPtCheckout] = useState(null); // powertranz booking

    useEffect(() => {
        if (open) setSelected(methods[0]?.key || "");
    }, [open, methods]);

    const finish = () => {
        setCheckout(null);
        setPtCheckout(null);
        onOpenChange(false);
        onPaid?.();
    };

    const proceed = async () => {
        if (!booking || !selected) return;
        setWorking(true);
        try {
            if (selected === "stripe") {
                const intent = await api.post("/payments/stripe/intent", { booking_id: booking.id });
                setCheckout({
                    booking,
                    clientSecret: intent.data.client_secret,
                    publishableKey: intent.data.publishable_key,
                });
                setWorking(false);
                return;
            }
            if (selected === "paypal") {
                toast.message(t("book.redirectingPaypal"));
                const order = await api.post("/payments/paypal/order", { booking_id: booking.id, origin: window.location.origin });
                window.location.href = order.data.approval_url;
                return;
            }
            if (selected === "powertranz") {
                setPtCheckout(booking);
                setWorking(false);
                return;
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("book.paymentFailed"));
            setWorking(false);
        }
    };

    return (
        <>
            <Dialog open={open && !checkout && !ptCheckout} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
                <DialogContent className="max-w-md" data-testid="pay-balance-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-3xl">{t("dashboard.payBalanceTitle")}</DialogTitle>
                        <DialogDescription>
                            {booking?.cabin_name} · {formatMoney(booking?.total_price || 0)}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="eyebrow">{t("dashboard.payChooseMethod")}</div>
                        <MethodPicker methods={methods} selected={selected} onSelect={setSelected} t={t} />
                        <button
                            onClick={proceed}
                            disabled={working || !selected}
                            data-testid="pay-balance-continue"
                            className="w-full px-6 py-4 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                            {working ? <><Loader2 size={14} className="animate-spin" /> {t("book.paying")}</> : t("dashboard.payContinue")}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {checkout && (
                <PaymentDialog
                    open={!!checkout}
                    booking={checkout.booking}
                    clientSecret={checkout.clientSecret}
                    publishableKey={checkout.publishableKey}
                    onClose={() => setCheckout(null)}
                    onSuccess={() => { toast.success(t("dashboard.paySuccess")); finish(); }}
                />
            )}
            {ptCheckout && (
                <PowerTranzDialog
                    open={!!ptCheckout}
                    booking={ptCheckout}
                    onClose={() => setPtCheckout(null)}
                    onSuccess={() => { toast.success(t("dashboard.paySuccess")); finish(); }}
                />
            )}
        </>
    );
}
